import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import {
  Badge,
  Button,
  Card,
  Container,
  Form,
  Modal,
  Spinner,
} from 'react-bootstrap'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import { FaMapMarkerAlt } from 'react-icons/fa'
import { LuGraduationCap } from 'react-icons/lu'
import { BsArrowClockwise, BsSearch } from 'react-icons/bs'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import ApiEndpoints from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import { useBusJourneyTracking } from './trackVehicle/useBusJourneyTracking'
import {
  BUS_ATTENDANT_HOME_ROUTE,
  isBusAttendantUser,
  shouldRedirectBusAttendantToPortal,
} from '../utils/busAttendant'

/** Poll latest-location while the tracking modal is open */
const LATEST_LOCATION_POLL_MS = 5 * 60 * 1000

const dash = (v) =>
  v === null || v === undefined || String(v).trim() === '' ? '—' : String(v)

function formatRelativeAgo(ms) {
  const sec = Math.max(0, Math.floor((Date.now() - ms) / 1000))
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 48) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  return `${day}d ago`
}

/**
 * Logged-in user matches this attendant row (journey controls, scoped list).
 * Handles both user-shaped rows (`id` = user id) and rows with separate `user_id`.
 */
function isAssignedAttendant(user, attendantRow) {
  if (!user || user.id == null || !attendantRow) return false
  const uid = Number(user.id)
  if (!Number.isFinite(uid)) return false
  const rowUserFk =
    attendantRow.user_id != null && attendantRow.user_id !== ''
      ? Number(attendantRow.user_id)
      : null
  const rowId =
    attendantRow.id != null && attendantRow.id !== ''
      ? Number(attendantRow.id)
      : null
  if (Number.isFinite(rowUserFk) && uid === rowUserFk) return true
  if (Number.isFinite(rowId) && uid === rowId) return true
  return false
}

/** Bus id if present on attendant payload (optional; usually joined from buses). */
function resolveBusIdFromAttendantRow(row) {
  if (!row || typeof row !== 'object') return null
  const raw =
    row.bus_id ??
    row.assigned_bus_id ??
    row.transport_bus_id ??
    row.busId ??
    (row.bus && (row.bus.bus_id ?? row.bus.id)) ??
    row.bus_id_fk
  if (raw == null || raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
}

/** Prefer bus assigned to this attendant (`bus_attendant_id` matches attendant `id`). */
function resolveBusForAttendant(attendant, buses) {
  if (!attendant || !Array.isArray(buses)) return null
  const aid = attendant.id ?? attendant.user_id
  if (aid == null || aid === '') return null
  const n = Number(aid)
  return buses.find((b) => Number(b?.bus_attendant_id) === n) ?? null
}

function resolveBusIdForAttendant(attendant, buses) {
  const inline = resolveBusIdFromAttendantRow(attendant)
  if (inline) return inline
  const bus = resolveBusForAttendant(attendant, buses)
  const bid = bus?.bus_id
  if (bid == null || bid === '') return null
  const n = Number(bid)
  return Number.isFinite(n) && n > 0 ? n : null
}

function displayAttendantName(row) {
  if (!row) return '—'
  return (
    row.name ||
    [row.first_name, row.last_name].filter(Boolean).join(' ') ||
    row.email ||
    '—'
  )
}

function VehicleTrackingModal({
  show,
  onHide,
  busNumber,
  attendantName,
  busId,
  latest,
  locationLoading,
  canManageJourney,
  journeyActive,
  onStartOrUpdate,
  onComplete,
  onRefresh,
  inFlight,
}) {
  const coords = latest?.coords
  const noLocationMessage = latest?.noLocationMessage
  const locationText =
    coords != null && Number.isFinite(coords.lat) && Number.isFinite(coords.long)
      ? `${coords.lat.toFixed(6)}°, ${coords.long.toFixed(6)}°`
      : noLocationMessage || '—'
  const lastMs = latest?.updatedMs ?? null
  const err = latest?.error

  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
      <Modal.Header
        closeButton
        className="text-dark border-0"
        style={{
          background: 'linear-gradient(135deg, #BDE0FE 0%, #e0f2fe 100%)',
          paddingBottom: '12px',
        }}
      >
        <div className="d-flex align-items-center gap-3 w-100">
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '14px',
              backgroundColor: '#223843',
              color: '#fff',
            }}
          >
            <FaMapMarkerAlt size={22} />
          </div>
          <div className="text-start min-w-0">
            <Modal.Title
              className="h5 mb-0 fw-bold"
              style={{ fontFamily: 'Inter, system-ui, sans-serif', color: '#0f172a' }}
            >
              {dash(busNumber)}
            </Modal.Title>
            <div
              className="text-truncate small mt-1"
              style={{ color: '#475569', maxWidth: '100%' }}
            >
              Attendant: {dash(attendantName)}
            </div>
          </div>
          <Badge
            bg={journeyActive ? 'success' : coords ? 'info' : 'secondary'}
            pill
            className="ms-auto flex-shrink-0 align-self-start"
            style={{ fontSize: '12px', padding: '6px 12px' }}
          >
            {journeyActive ? 'Journey live' : coords ? 'Last known' : 'No position'}
          </Badge>
        </div>
      </Modal.Header>
      <Modal.Body className="pt-4 pb-2" style={{ backgroundColor: '#f8fafc' }}>
        <div
          className="position-relative overflow-hidden rounded-4 mb-4 border"
          style={{
            borderColor: '#e2e8f0',
            background:
              'linear-gradient(160deg, #e0f2fe 0%, #f0f9ff 40%, #f8fafc 100%)',
            minHeight: '200px',
          }}
        >
          <div
            className="position-absolute opacity-25"
            style={{
              right: '-20px',
              top: '-20px',
              width: '180px',
              height: '180px',
              borderRadius: '50%',
              background: '#38bdf8',
              filter: 'blur(40px)',
            }}
          />
          <div className="position-relative p-4 p-md-5">
            <p
              className="text-uppercase fw-semibold mb-2"
              style={{
                fontSize: '11px',
                letterSpacing: '0.08em',
                color: '#64748b',
              }}
            >
              Latest coordinates
            </p>
            {locationLoading ? (
              <div className="d-flex align-items-center gap-2 py-2">
                <Spinner animation="border" size="sm" variant="primary" />
                <span className="text-muted small">Loading location…</span>
              </div>
            ) : (
              <p
                className="mb-0 fw-semibold"
                style={{
                  fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                  lineHeight: 1.5,
                  color: coords ? '#0f172a' : '#475569',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                {locationText}
              </p>
            )}
            {err ? (
              <p className="text-danger small mt-2 mb-0">{err}</p>
            ) : null}
          </div>
        </div>

        <Card
          className="border-0 shadow-sm mb-3"
          style={{ borderRadius: '12px', backgroundColor: '#ffffff' }}
        >
          <Card.Body className="py-3 px-4">
            <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
              <div>
                <div
                  className="fw-semibold mb-1"
                  style={{ fontSize: '13px', color: '#64748b' }}
                >
                  Last updated
                </div>
                <div style={{ fontSize: '15px', color: '#0f172a' }}>
                  {lastMs == null ? (
                    <span className="text-muted">
                      {noLocationMessage
                        ? 'No timestamp (awaiting first location share)'
                        : 'No server position yet'}
                    </span>
                  ) : (
                    <>
                      {new Date(lastMs).toLocaleString()}
                      <span className="text-muted ms-2" style={{ fontSize: '13px' }}>
                        ({formatRelativeAgo(lastMs)})
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div
                className="text-end small text-muted"
                style={{ maxWidth: '280px', fontSize: '12px' }}
              >
                {canManageJourney
                  ? 'Start journey sends your current GPS. While live, you can send updates anytime; this screen also refetches the last saved position about every 5 minutes while open.'
                  : 'Location is updated when the assigned bus attendant shares GPS during an active journey. Last saved position refetches about every 5 minutes while this window is open.'}
              </div>
            </div>
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer
        className="border-0 pt-0 gap-2 flex-wrap"
        style={{ backgroundColor: '#f8fafc' }}
      >
        <Button variant="outline-secondary" onClick={onHide} className="px-4">
          Close
        </Button>
        <Button
          variant="outline-primary"
          className="px-3"
          disabled={!busId || inFlight?.[Number(busId)]}
          onClick={() => onRefresh(busId)}
        >
          Refresh location
        </Button>
        {canManageJourney ? (
          <>
            <Button
              variant="success"
              className="px-3 fw-semibold"
              disabled={!busId || inFlight?.[Number(busId)] === 'start'}
              onClick={() => onStartOrUpdate(busId)}
            >
              {inFlight?.[Number(busId)] === 'start' ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Sending…
                </>
              ) : journeyActive ? (
                'Send current location'
              ) : (
                'Start journey (GPS)'
              )}
            </Button>
            <Button
              variant="danger"
              className="px-3 fw-semibold"
              disabled={
                !busId || !journeyActive || inFlight?.[Number(busId)] === 'complete'
              }
              onClick={() => onComplete(busId)}
            >
              {inFlight?.[Number(busId)] === 'complete' ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Completing…
                </>
              ) : (
                'Complete journey'
              )}
            </Button>
          </>
        ) : null}
      </Modal.Footer>
    </Modal>
  )
}

function TrackVehicleCell({
  busId,
  attendantName,
  busNumber,
  journeyActive,
  canManageJourney,
  inFlight,
  onOpenModal,
}) {
  const live = Boolean(journeyActive)
  return (
    <div className="d-flex flex-column align-items-start gap-2">
      <Badge bg={live ? 'success' : 'secondary'} pill className="fw-normal">
        {live ? 'Journey live' : 'View map'}
      </Badge>
      <Button
        type="button"
        size="sm"
        className="fw-semibold d-inline-flex align-items-center gap-1"
        style={{
          fontSize: '12px',
          borderRadius: '8px',
          backgroundColor: '#223843',
          border: 'none',
          color: '#fff',
        }}
        disabled={Boolean(inFlight?.[Number(busId)]) || busId == null}
        title={busId == null ? 'No bus is linked to this attendant for tracking.' : undefined}
        onClick={() =>
          onOpenModal({
            busId,
            attendantName,
            busNumber,
            canManageJourney,
          })
        }
      >
        <FaMapMarkerAlt size={14} />
        View location
      </Button>
    </div>
  )
}

export default function TrackVehicle() {
  const { logout, user } = useAuth()
  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo, name } = useInstitute()

  const [attendants, setAttendants] = useState([])
  /** Used only to join bus number / driver / contact and resolve `bus_id` for tracking. */
  const [buses, setBuses] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [journeyActiveByBusId, setJourneyActiveByBusId] = useState({})
  const [trackModal, setTrackModal] = useState(null)
  const [modalLocationLoading, setModalLocationLoading] = useState(false)

  const {
    latestByBusId,
    fetchLatest,
    startOrUpdateLocation,
    completeJourney,
    inFlight,
  } = useBusJourneyTracking()

  const openTrackModal = useCallback((params) => {
    setTrackModal(params)
  }, [])

  const closeTrackModal = useCallback(() => setTrackModal(null), [])

  const loadAttendantsAndBuses = useCallback(async () => {
    setLoading(true)
    try {
      await Promise.all([
        (async () => {
          try {
            const res = await ApiEndpoints.getBusAttendants()
            const list = res.data?.data ?? res.data ?? []
            setAttendants(Array.isArray(list) ? list : [])
          } catch (error) {
            setAttendants([])
            toast.error(
              error?.response?.data?.message ||
                error?.message ||
                'Could not load bus attendants',
            )
          }
        })(),
        (async () => {
          try {
            const res = await ApiEndpoints.getAllBuses()
            const list = res.data?.data ?? res.data ?? []
            setBuses(Array.isArray(list) ? list : [])
          } catch (error) {
            setBuses([])
            toast.error(
              error?.response?.data?.message ||
                error?.message ||
                'Could not load buses (driver / bus number columns stay empty until this succeeds).',
            )
          }
        })(),
      ])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAttendantsAndBuses()
  }, [loadAttendantsAndBuses])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  /** Bus attendants only ever see their own assignment; staff see everyone. */
  const attendantsVisibleToUser = useMemo(() => {
    if (!isBusAttendantUser(user)) return attendants
    return attendants.filter((row) => isAssignedAttendant(user, row))
  }, [attendants, user])

  const filteredAttendants = useMemo(() => {
    if (!search.trim()) return attendantsVisibleToUser
    const q = search.trim().toLowerCase()
    return attendantsVisibleToUser.filter((row) => {
      const bus = resolveBusForAttendant(row, buses)
      const busHay = bus
        ? [
            bus.bus_number,
            bus.driver_name,
            bus.driver_contact_number,
            bus.bus_owner_name,
          ]
            .filter((v) => v != null && v !== '')
            .join(' ')
            .toLowerCase()
        : ''
      const rowMatch = Object.values(row).some(
        (val) =>
          val != null &&
          val !== '' &&
          String(val).toLowerCase().includes(q),
      )
      return rowMatch || busHay.includes(q)
    })
  }, [attendantsVisibleToUser, buses, search])

  const handleStartOrUpdate = useCallback(
    async (busId) => {
      try {
        await startOrUpdateLocation(busId)
        setJourneyActiveByBusId((prev) => ({ ...prev, [busId]: true }))
      } catch {
        /* toast in hook */
      }
    },
    [startOrUpdateLocation],
  )

  const handleComplete = useCallback(
    async (busId) => {
      try {
        await completeJourney(busId)
        setJourneyActiveByBusId((prev) => ({ ...prev, [busId]: false }))
      } catch {
        /* toast in hook */
      }
    },
    [completeJourney],
  )

  /** While modal is open and journey is live, periodically push GPS for this bus. */
  useEffect(() => {
    if (!trackModal?.busId || !trackModal?.canManageJourney) return
    if (!journeyActiveByBusId[trackModal.busId]) return
    const id = Number(trackModal.busId)
    const tick = () => {
      startOrUpdateLocation(id).catch(() => {})
    }
    const t = window.setInterval(tick, 90000)
    return () => window.clearInterval(t)
  }, [trackModal, journeyActiveByBusId, startOrUpdateLocation])

  /**
   * Latest location + journey status only for the bus whose modal is open.
   * Poll latest-location every 5 minutes while the modal stays open.
   */
  useEffect(() => {
    if (trackModal?.busId == null) {
      setModalLocationLoading(false)
      return
    }
    const id = Number(trackModal.busId)
    if (!id) return

    let cancelled = false

    const syncJourneyFromBus = async () => {
      try {
        const res = await ApiEndpoints.getBusById(id)
        if (cancelled) return
        const raw = res.data?.data ?? res.data
        const st = raw?.bus_journey_status
        if (st === 'started') {
          setJourneyActiveByBusId((p) => ({ ...p, [id]: true }))
        } else if (st === 'completed' || st === null) {
          setJourneyActiveByBusId((p) => ({ ...p, [id]: false }))
        }
      } catch {
        /* ignore */
      }
    }

    const load = async () => {
      setModalLocationLoading(true)
      try {
        await fetchLatest(id)
      } finally {
        if (!cancelled) setModalLocationLoading(false)
      }
      if (!cancelled) await syncJourneyFromBus()
    }

    load()
    const poll = window.setInterval(() => {
      if (!cancelled) fetchLatest(id)
    }, LATEST_LOCATION_POLL_MS)

    return () => {
      cancelled = true
      window.clearInterval(poll)
    }
  }, [trackModal?.busId, fetchLatest])

  const tableHeaders = useMemo(
    () => [
      'Bus number',
      'Driver',
      'Attendant',
      'Attendant Email',
      'Attendant contact',
      // 'Track vehicle',
    ],
    [],
  )

  const attendantIsUser = isBusAttendantUser(user)

  if (shouldRedirectBusAttendantToPortal(user)) {
    return <Navigate to={BUS_ATTENDANT_HOME_ROUTE} replace />
  }

  return (
    <>
      <style>
        {`
          .page-title {
            font-family: 'Inter', system-ui, sans-serif;
            letter-spacing: -0.02em;
            color: #223843;
          }
        `}
      </style>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: '12px',
          paddingRight: '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: isMobile ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
                gap: '12px',
              }}
            >
              <div
                className="d-flex align-items-center gap-3"
                style={{ flex: 1, minWidth: 0 }}
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <h3
                    className="m-0 fw-bold page-title text-start"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: isMobile ? '14px' : 'clamp(18px, 2.5vw, 22px)',
                      lineHeight: '1.2',
                    }}
                  >
                    {attendantIsUser ? 'My vehicle' : 'Vehicle Attendant'}
                  </h3>
                  <p
                    className="m-0 mt-0 text-start"
                    style={{ fontSize: isMobile ? '10px' : '13px', color: '#6b7280' }}
                  >
                    While the window is open it refreshes about every 5 minutes.{' '}
                    {attendantIsUser
                      ? 'Only your assigned bus appears here. Use Start journey to share GPS; complete the journey when finished.'
                      : 'Assigned attendants share location during an active journey.'}
                  </p>
                </div>
              </div>
              {!isMobile && (
                <div className="d-flex align-items-center gap-3" style={{ justifyContent: 'flex-end' }}>
                  <ProfileDropdown
                    logout={logout}
                    notificationCount={3}
                    showNotifications={true}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <Container
          fluid
          className="p-0 mt-4"
          style={{
            minHeight: '60vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
          }}
        >
          <Card
            className="border-0 shadow-sm mb-4"
            style={{
              borderRadius: '12px',
              border: '1px solid #D1D3D4',
              overflow: 'hidden',
              backgroundColor: '#ffffff',
            }}
          >
            <div
              style={{
                padding: '20px',
                borderBottom: '1px solid #F3F4F6',
                background: '#ffffff',
              }}
            >
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3" />
                <button
                  type="button"
                  onClick={() => {
                    setSearch('')
                    loadAttendantsAndBuses()
                  }}
                  disabled={loading}
                  title="Refresh attendants and bus assignments"
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: loading ? '#9ca3af' : '#223843',
                    border: 'none',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <BsArrowClockwise size={20} />
                </button>
              </div>
            </div>
            <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
              {!attendantIsUser ? (
                <div style={{ position: 'relative' }} className="mb-4">
                  <label
                    className="fw-semibold text-start mb-2 d-block"
                    style={{
                      color: '#6b7280',
                      fontSize: '13px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Search attendants
                  </label>
                  <div style={{ position: 'relative' }}>
                    <BsSearch
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#6b7280',
                        fontSize: '16px',
                        zIndex: 1,
                      }}
                    />
                    <Form.Control
                      type="text"
                      placeholder="Search by attendant, email, bus number, driver, or any field"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{
                        paddingLeft: '40px',
                        borderRadius: '8px',
                        border: '1px solid #D1D3D4',
                        fontSize: '14px',
                        height: '40px',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#223843'
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#D1D3D4'
                      }}
                    />
                  </div>
                </div>
              ) : null}

              <Card
                className="table-card m-0"
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  overflow: 'visible',
                  backgroundColor: '#ffffff',
                  position: 'relative',
                }}
              >
                <div
                  className="table-wrapper"
                  style={{
                    overflowX: 'auto',
                    overflowY: 'visible',
                    position: 'relative',
                    borderRadius: '12px',
                  }}
                >
                  {loading ? (
                    <div
                      className="d-flex flex-column align-items-center justify-content-center py-5 gap-2"
                      style={{ minHeight: '200px' }}
                    >
                      <Spinner animation="border" variant="primary" />
                      <span style={{ fontSize: '14px', color: '#6b7280' }}>Loading attendants…</span>
                    </div>
                  ) : (
                    <Table className="table mb-0" style={{ marginBottom: 0 }}>
                      <Thead>
                        <Tr style={{ backgroundColor: '#F3F4F6' }}>
                          {tableHeaders.map((h) => (
                            <Th
                              key={h}
                              style={{
                                fontSize: '13px',
                                fontWeight: '600',
                                padding: '16px',
                                border: 'none',
                                borderBottom: '1px solid #D1D3D4',
                                textAlign: 'left',
                                color: '#111827',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {h}
                            </Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {filteredAttendants.length === 0 ? (
                          <Tr>
                            <Td
                              colSpan={tableHeaders.length}
                              style={{
                                textAlign: 'center',
                                padding: '24px',
                                color: '#6b7280',
                                fontSize: '14px',
                              }}
                            >
                              {(() => {
                                if (search.trim()) {
                                  return attendantIsUser
                                    ? 'Nothing matches your search.'
                                    : 'No attendants found for this search.'
                                }
                                if (attendantIsUser) {
                                  if (attendants.length === 0) {
                                    return 'Could not load assignment data. Try refreshing, or contact your administrator.'
                                  }
                                  return attendantsVisibleToUser.length === 0
                                    ? 'No vehicle is linked to your account yet. Ask your administrator to assign you as the attendant on your bus.'
                                    : 'Nothing matches your search.'
                                }
                                return 'No bus attendants found.'
                              })()}
                            </Td>
                          </Tr>
                        ) : (
                          filteredAttendants.map((row, index) => {
                            const assignedBus = resolveBusForAttendant(row, buses)
                            const busId = resolveBusIdForAttendant(row, buses)
                            const attendantLabel = displayAttendantName(row)
                            const busNumberLabel = assignedBus?.bus_number
                            const canManage =
                              attendantIsUser && isAssignedAttendant(user, row)
                            const rowKey = row.id ?? row.user_id ?? index
                            return (
                              <Tr
                                key={String(rowKey)}
                                style={{
                                  backgroundColor:
                                    index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                                  transition: 'background-color 0.2s ease',
                                  borderBottom: '1px solid #E5E7EB',
                                  textAlign: 'left',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    'rgba(37, 99, 235, 0.06)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)'
                                }}
                              >
                                <Td
                                  style={{
                                    fontSize: '14px',
                                    padding: '16px',
                                    border: 'none',
                                    color: '#111827',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                  }}
                                >
                                  {dash(busNumberLabel)}
                                </Td>
                                <Td
                                  style={{
                                    fontSize: '14px',
                                    padding: '16px',
                                    border: 'none',
                                    color: '#111827',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                  }}
                                >
                                  {dash(assignedBus?.driver_name)}
                                </Td>
                                <Td
                                  style={{
                                    fontSize: '14px',
                                    padding: '16px',
                                    border: 'none',
                                    color: '#111827',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                  }}
                                >
                                  {dash(attendantLabel)}
                                </Td>
                                <Td
                                  style={{
                                    fontSize: '14px',
                                    padding: '16px',
                                    border: 'none',
                                    color: '#111827',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                  }}
                                >
                                  {dash(row.email)}
                                </Td>
                                <Td
                                  style={{
                                    fontSize: '14px',
                                    padding: '16px',
                                    border: 'none',
                                    color: '#111827',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                  }}
                                >
                                  {dash(row?.phone_number)}
                                </Td>
                                {/* <Td
                                  style={{
                                    fontSize: '14px',
                                    padding: '16px',
                                    border: 'none',
                                    color: '#111827',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                    verticalAlign: 'top',
                                  }}
                                >
                                  <TrackVehicleCell
                                    busId={busId}
                                    attendantName={attendantLabel}
                                    busNumber={busNumberLabel ?? '—'}
                                    journeyActive={journeyActiveByBusId[Number(busId)]}
                                    canManageJourney={canManage}
                                    inFlight={inFlight}
                                    onOpenModal={openTrackModal}
                                  />
                                </Td> */}
                              </Tr>
                            )
                          })
                        )}
                      </Tbody>
                    </Table>
                  )}
                </div>
              </Card>
            </Card.Body>
          </Card>
        </Container>
      </div>

      {trackModal != null && (
        <VehicleTrackingModal
          show
          onHide={closeTrackModal}
          busNumber={trackModal.busNumber}
          attendantName={trackModal.attendantName}
          busId={trackModal.busId}
          latest={latestByBusId[Number(trackModal.busId)]}
          locationLoading={modalLocationLoading}
          canManageJourney={trackModal.canManageJourney}
          journeyActive={Boolean(journeyActiveByBusId[Number(trackModal.busId)])}
          onStartOrUpdate={handleStartOrUpdate}
          onComplete={handleComplete}
          onRefresh={fetchLatest}
          inFlight={inFlight}
        />
      )}
    </>
  )
}
